/**
 * Mg Slider Core — single source of truth for every Splide-backed carousel.
 * (c) 2026 NAMY SOLUTIONS LLC — All Rights Reserved.
 *
 * window.MgSlider.mount(root, opts) builds the nav (arrows + middle), mounts
 * Splide, and drives the progress bar. ONE place owns arrow direction, so the
 * historical scaleX(-1) flip trap cannot recur: prev always renders a
 * left-chevron, next a right-chevron — zero transform on the icon.
 *
 * opts:
 *   arrows      {boolean}  show prev/next arrows (default true)
 *   pagination  {'none'|'dots'|'progress'}  middle nav (default 'none')
 *   labels      {{prev,next}}  localized aria-labels (fallback EN)
 *   config      {object}   extra Splide options merged over defaults
 *                          (perPage, gap, autoWidth, focus, autoplay, ...)
 */
(function () {
  'use strict';
  if (window.MgSlider) return;

  // Distinct, correctly-oriented chevrons. NEVER flipped via CSS.
  var CHEVRON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"' +
    ' fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"' +
    ' stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="PATH"/></svg>';
  var ICON_PREV = CHEVRON.replace('PATH', 'm15 18-6-6 6-6'); // points left
  var ICON_NEXT = CHEVRON.replace('PATH', 'm9 18 6-6-6-6'); // points right

  function buildNav(opts) {
    var nav = document.createElement('div');
    nav.className = 'splide__arrows mg-slider__nav';

    if (opts.arrows !== false) {
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'splide__arrow splide__arrow--prev mg-slider__arrow';
      prev.setAttribute('aria-label', (opts.labels && opts.labels.prev) || 'Previous');
      prev.innerHTML = ICON_PREV;
      nav.appendChild(prev);
    }

    if (opts.pagination === 'progress') {
      var track = document.createElement('div');
      track.className = 'splide__progress';
      var bar = document.createElement('div');
      bar.className = 'splide__progress__bar';
      track.appendChild(bar);
      nav.appendChild(track);
    }

    if (opts.arrows !== false) {
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'splide__arrow splide__arrow--next mg-slider__arrow';
      next.setAttribute('aria-label', (opts.labels && opts.labels.next) || 'Next');
      next.innerHTML = ICON_NEXT;
      nav.appendChild(next);
    }

    // Dots without arrows still need the centred row to land in.
    return nav.childNodes.length || opts.pagination === 'dots' ? nav : null;
  }

  function mount(root, opts) {
    opts = opts || {};
    if (!root || root.dataset.namySliderMounted) return null;

    // Order-independent: retry once Splide is defined (deferred-script safe).
    if (typeof window.Splide !== 'function') {
      if (document.readyState === 'complete') {
        opts._sliderRetries = (opts._sliderRetries || 0) + 1;
        if (opts._sliderRetries <= 5) setTimeout(function () { mount(root, opts); }, 100);
      } else {
        window.addEventListener('load', function () { mount(root, opts); }, { once: true });
      }
      return null;
    }
    root.dataset.namySliderMounted = 'true';

    /* Splide LIT `aria-label` sur la racine au constructeur
       (`label: getAttribute(root,'aria-label') || ''`) et son `destroy()` le
       retire. Au remontage — ce que fait l'editeur de theme a chaque
       modification — il relit donc une chaine vide et la region reste SANS NOM
       accessible, definitivement. Mesure : de deux carrousels identiques, seul
       celui qui n'avait jamais ete detache portait encore son nom.
       On repose l'attribut avant chaque montage : c'est la seule facon qu'il
       survive au cycle. */
    if (opts.label) root.setAttribute('aria-label', opts.label);

    var pag = opts.pagination || 'none';

    // Inject the nav BEFORE mount so Splide binds to our custom arrows
    // (rather than auto-injecting its own identical-SVG arrows).
    var nav = buildNav(opts);
    if (nav) root.appendChild(nav);

    var config = Object.assign(
      {
        type: 'slide',
        gap: 0,
        omitEnd: true,
        perPage: 1,
        pauseOnHover: true,
        pauseOnFocus: true,
        i18n: {
          prev: (opts.labels && opts.labels.prev) || 'Previous',
          next: (opts.labels && opts.labels.next) || 'Next'
        }
      },
      opts.config || {}
    );
    config.arrows = opts.arrows !== false;
    config.pagination = pag === 'dots';

    var splide;
    try {
      splide = new window.Splide(root, config);
      splide.mount();
    } catch (e) {
      if (window.Mg) Mg.debug('MgSlider', e);
      return null;
    }

    // For dots mode, move Splide's injected pagination into the nav row
    // so the chrome is always [prev] [indicator] [next] (centered, 2026).
    if (pag === 'dots' && nav) {
      var pagEl = root.querySelector('.splide__pagination');
      if (pagEl) nav.insertBefore(pagEl, nav.querySelector('.splide__arrow--next'));
    }

    // Progress bar driven by Splide events (bundle has no native progress).
    // scaleX on the BAR (GPU, no layout) — unrelated to arrow orientation.
    if (pag === 'progress') {
      var update = function () {
        var len = splide.length || 1;
        var idx = splide.index || 0;
        var rate = len > 1 ? Math.min(1, (idx + 1) / len) : 1;
        root.style.setProperty('--mg-slider-progress', rate);
      };
      splide.on('mounted move', update);
      update();
    }

    return splide;
  }

  function normalizePagination(v) {
    return v === 'progress' || v === 'dots' ? v : (v === 'true' ? 'dots' : 'none');
  }

  function wrapList(list) {
    var track = document.createElement('div');
    track.className = 'splide__track';
    list.parentNode.insertBefore(track, list);
    track.appendChild(list);
    var root = document.createElement('div');
    root.className = 'splide';
    track.parentNode.insertBefore(root, track);
    root.appendChild(track);
    return root;
  }

  /* Le demontage appartient a CELUI QUI POSE la marque. `mount` ecrit
     `data-mg-slider-mounted` sur la racine pour refuser un double montage ;
     sans effacement, cette marque survit a `destroy()` et le carrousel ne
     revient JAMAIS — mesure : le composant perd 7 noeuds au ré-attachement et
     ne les recupere pas. Les consommateurs appellent ceci plutot que de toucher
     a l'etat interne du coeur. */
  function unmount(splide) {
    if (!splide) return;
    var root = splide.root;
    try { splide.destroy(); } catch (e) { void e; }
    if (root) {
      /* `destroy()` ne defait que le DOM de Splide. La navigation, elle, est
         NOTRE ouvrage : sans ce retrait, chaque remontage en ajoute un second
         jeu — mesure : 77 noeuds deviennent 91 apres deux ré-attachements, soit
         l'exces exact d'une paire de fleches. On ne retire que ce que `mount` a
         construit, jamais le balisage du marchand. */
      var navs = root.querySelectorAll(':scope > .mg-slider__nav');
      for (var i = 0; i < navs.length; i += 1) navs[i].remove();
      if (root.dataset) delete root.dataset.namySliderMounted;
    }
  }

  window.MgSlider = { mount: mount, unmount: unmount, normalizePagination: normalizePagination, wrapList: wrapList };
})();
